import { useCallback, useEffect, useRef } from 'react'
import { useAuth } from '@/lib/AuthContext'
import { useCommunity } from '@/lib/CommunityContext'
import { sdk } from '@/lib/sdk'
import { COMMUNITY_SUSPENDED_EVENT, type MemberEntryView } from '@/lib/memberEntry'

/** A reload on returning to the tab waits at least this long after the previous one. */
const FOCUS_REFRESH_MS = 60_000

/**
 * F-86: the community's suspension as the server showed it to this viewer.
 * `reason` is only there for the leadership and the SuperAdmin; the screens
 * use its presence, never a role computed here.
 */
export function useMemberEntry() {
  const { community } = useCommunity()
  const entry = (community as { memberEntry?: MemberEntryView } | null)?.memberEntry
  return {
    suspended: Boolean(entry?.suspended),
    reason: entry?.suspended ? entry.reason || null : null,
    suspendedAt: entry?.suspended ? entry.suspendedAt || null : null
  }
}

/**
 * Keeps `community.memberEntry` current. Mounted once, by the app layout.
 *
 * The community is read at boot, often before sign-in, when the server cannot
 * tell the leadership apart and sends no reason. This reloads it once per
 * person and profile, when a request is refused because the community was
 * suspended meanwhile, and when the tab comes back after a while (the
 * SuperAdmin may have lifted it).
 */
export function useMemberEntrySync() {
  const { user } = useAuth()
  const { community, setCommunity } = useCommunity()
  const domain = community?.domain
  const lastRefresh = useRef(0)
  const viewer = user ? `${user.id}:${user.role}` : null
  /* Boot already read the community signed out; any other change of viewer
     (sign-in, profile switch, sign-out) reloads it. */
  const lastViewer = useRef<string | null>(null)

  const refresh = useCallback(async () => {
    if (!domain) return
    lastRefresh.current = Date.now()
    try {
      const fresh = await sdk.communities.getCommunityByDomain(domain)
      if (!fresh) return
      const memberEntry: MemberEntryView = (fresh as { memberEntry?: MemberEntryView }).memberEntry ?? { suspended: false }
      /* F-143: the sending reputation is team-only too, so it comes with the signed-in reload. */
      const sendingReputation = (fresh as { sendingReputation?: unknown }).sendingReputation ?? null
      setCommunity((prev) => (prev ? ({ ...prev, memberEntry, sendingReputation } as typeof prev) : prev))
    } catch {
      // Keeps what it had: a failed reload must not hide a suspension.
    }
  }, [domain, setCommunity])

  useEffect(() => {
    if (!community?.id || viewer === lastViewer.current) return
    lastViewer.current = viewer
    void refresh()
  }, [viewer, community?.id, refresh])

  useEffect(() => {
    const onSuspended = () => void refresh()
    const onVisible = () => {
      if (document.visibilityState === 'visible' && Date.now() - lastRefresh.current > FOCUS_REFRESH_MS) void refresh()
    }
    window.addEventListener(COMMUNITY_SUSPENDED_EVENT, onSuspended)
    document.addEventListener('visibilitychange', onVisible)
    return () => {
      window.removeEventListener(COMMUNITY_SUSPENDED_EVENT, onSuspended)
      document.removeEventListener('visibilitychange', onVisible)
    }
  }, [refresh])
}
